import './index.scss';

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import MarkWhite from '@/assets/storage/HeaderWhite.svg';

type ActionsSlotContextValue = {
  slot: HTMLElement | null;
  setSlot: (node: HTMLElement | null) => void;
};

const ActionsSlotContext = createContext<ActionsSlotContextValue | null>(null);

/** Lets a page's toolbar render its page-level actions at the right of the page banner. */
export const PageBannerActionsProvider = ({ children }: { children: ReactNode }) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ slot, setSlot }), [slot]);

  return <ActionsSlotContext.Provider value={value}>{children}</ActionsSlotContext.Provider>;
};

/** Renders page-level actions inside the page banner, or in place when the page has no banner. */
export const PageBannerActions = ({
  children,
  fallbackClassName,
}: {
  children: ReactNode;
  fallbackClassName?: string;
}) => {
  const slot = useContext(ActionsSlotContext)?.slot;

  return slot ? createPortal(children, slot) : <div className={fallbackClassName}>{children}</div>;
};

export type PageBannerProps = {
  title: string;
  /** Highlighted part of the title; without it the last word of `title` is highlighted. */
  titleTwo?: string;
  /** One short sentence under the title. */
  description?: string;
  /** Rendered under the title, e.g. the entity's status or summary. */
  meta?: ReactNode;
  /** Rendered at the right of the banner; without it the page toolbar's banner actions go there. */
  actions?: ReactNode;
};

/** Splits a title into its lead and its last word, which the Keyvod titles highlight. */
export const splitLastWord = (text: string): [string, string] => {
  const trimmed = text.trimEnd();
  const index = trimmed.lastIndexOf(' ');

  return index < 0 ? ['', trimmed] : [trimmed.slice(0, index + 1), trimmed.slice(index + 1)];
};

export const PageBanner = ({ title, titleTwo, description, meta, actions }: PageBannerProps) => {
  const setSlot = useContext(ActionsSlotContext)?.setSlot;
  const [lead, highlight] = titleTwo ? [`${title} `, titleTwo] : splitLastWord(title);

  return (
    <section className="c-pageBanner">
      <span className="c-pageBanner__mark" aria-hidden="true">
        <img src={MarkWhite} alt="" className="c-pageBanner__markImage" />
      </span>
      <div className="c-pageBanner__id">
        <h2 className="c-pageBanner__title">
          {lead}
          <span className="c-pageBanner__titleHighlight">{highlight}</span>
        </h2>
        {description ? <p className="c-pageBanner__desc">{description}</p> : null}
        {meta ? <div className="c-pageBanner__meta">{meta}</div> : null}
      </div>
      {actions ? (
        <div className="c-pageBanner__actions">{actions}</div>
      ) : (
        <div className="c-pageBanner__actions" ref={setSlot} />
      )}
    </section>
  );
};
